import React, { useState, useEffect } from 'react';
import { PMT2Product } from '@/types/pmt2Types';
import { parseNumericValue } from '@/services/pmt2Utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableRow, TableHead, TableHeader } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { fetchLatestOldValuesMap } from '@/services/pmt3OldValuesService';

interface TargetsDashboardProps {
  product: PMT2Product | null;
}

type OldValuesMap = Record<string, string>;

const TargetsDashboard: React.FC<TargetsDashboardProps> = ({ product }) => {
  const [oldValuesMap, setOldValuesMap] = useState<OldValuesMap | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // --- Formatting & Utility Helpers ---
  const formatInteger = (value: any): string => {
    const num = parseNumericValue(value);
    if (num === null) return '-';
    return Math.round(num).toLocaleString('en-US');
  };

  const formatDefault = (value: any): string => {
    if (value === null || value === undefined || value === '') return '-';
    return String(value);
  };
  // --- End Helpers ---

  // Fetch Old Values Map
  useEffect(() => {
    const fetchMap = async () => {
      setIsLoading(true);
      setOldValuesMap(null);

      if (product?.companyName && product?.id) {
        try {
          const map = await fetchLatestOldValuesMap(product.companyName, product.id);
          setOldValuesMap(map);
        } catch (error) {
          console.error("Error fetching old values map for Targets:", error);
          setOldValuesMap(null);
        } finally {
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
    };

    fetchMap();
  }, [product]);

  // Configuration for dashboard items
  const targetsItemsConfig = [
    { label: 'Total Positive Targets', valueKey: 'Total Positive Targets', oldValueKey: 'Total Positive Targets', format: 'integer' },
    { label: 'Total Negative Targets', valueKey: 'Total Negative Targets', oldValueKey: 'Total Negative Targets', format: 'integer' },
    { label: 'Exact', valueKey: 'Exact', oldValueKey: 'Exact', format: 'integer' },
    { label: 'PTA', valueKey: 'PTA', oldValueKey: 'PTA', format: 'integer' },
    { label: 'CTA', valueKey: 'CTA', oldValueKey: 'CTA', format: 'integer' },
    { label: 'Broad', valueKey: 'Broad', oldValueKey: 'Broad', format: 'integer' },
    { label: 'Negative Exact', valueKey: 'Negative Exact', oldValueKey: 'Negative Exact', format: 'integer' },
    { label: 'Negative Phrase', valueKey: 'Negative Phrase', oldValueKey: 'Negative Phrase', format: 'integer' },
    { label: 'Negative PTA', valueKey: 'Negative PTA', oldValueKey: 'Negative PTA', format: 'integer' },
  ];

  // Helper to format display values
  const formatDisplay = (value: any, formatType?: string): string => {
      if (value === null || value === undefined || value === '') return '-';
      switch (formatType) {
          case 'integer': return formatInteger(value);
          default: return formatDefault(value);
      }
  };

  if (!product) {
       // Apply width constraint to the placeholder card as well
       return (
            <div className="max-w-lg mx-auto"> {/* Width constraint wrapper */}
                <Card>
                    <CardHeader><CardTitle className="text-2xl font-bold">Targets</CardTitle></CardHeader>
                    <CardContent><p className="text-sm text-gray-500">Select a product to view Targets.</p></CardContent>
                </Card>
            </div>
        );
  }

  return (
    // Apply width constraint to the main card wrapper
    <div className="max-w-lg mx-auto">
        <Card>
          <CardHeader className="p-4">
            <CardTitle className="text-2xl font-bold">Targets</CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            {/* Removed the extra centering divs */}
            <Table>
                <TableHeader>
                    <TableRow>
                        {/* Removed explicit width, ensure text-left (default) */}
                        <TableHead className="text-left">Item</TableHead>
                        <TableHead className="text-right">Value</TableHead>
                        <TableHead className="text-right">Old Value</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {targetsItemsConfig.map((item) => {
                        const currentValue = item.valueKey ? product[item.valueKey] : undefined;
                        const displayValue = formatDisplay(currentValue, item.format);

                        const oldValue = item.oldValueKey ? oldValuesMap?.[item.oldValueKey] : undefined;
                        const formattedOldValue = formatDisplay(oldValue, item.format);
                        const displayOldValueHighlight = formattedOldValue !== '-' && String(displayValue) !== formattedOldValue;

                        return (
                            <TableRow key={item.label}>
                                {/* Ensure text-left (default) */}
                                <TableCell className="font-medium py-2 px-4 text-left">{item.label}</TableCell>
                                <TableCell className="text-right py-2 px-4">
                                    {isLoading ? <Skeleton className="h-4 w-16 float-right" /> : displayValue}
                                </TableCell>
                                <TableCell className="text-right py-2 px-4">
                                    {isLoading ? <Skeleton className="h-4 w-16 float-right" /> : (
                                        displayOldValueHighlight ? (
                                            <span className="bg-orange-100 dark:bg-orange-800 px-1.5 py-0.5 rounded text-orange-800 dark:text-orange-100">
                                                {formattedOldValue}
                                            </span>
                                        ) : (
                                            <span>-</span>
                                        )
                                    )}
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
          </CardContent>
        </Card>
    </div>
  );
};

export default TargetsDashboard;